import WebApp from '@twa-dev/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BarChart2,
  RefreshCw,
  Server,
  Shield,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import {
  AdminActivityUser,
  AdminBackup,
  AdminDbStat,
  AdminStats,
  AdminUsage,
  AdminUser,
  AdminUserDetail,
  BroadcastResult,
  PaginatedUsers,
  PromoSchema,
  RevenueData,
  api,
} from '../api/client'

const _API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
import { Button } from '../components/ui/Button'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { usePricing } from '../hooks/usePricing'

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const delta = (Date.now() - new Date(iso).getTime()) / 1000
  if (delta < 60) return 'только что'
  if (delta < 3600) return `${Math.floor(delta / 60)}м назад`
  if (delta < 86400) return `${Math.floor(delta / 3600)}ч назад`
  const days = Math.floor(delta / 86400)
  return days === 1 ? 'вчера' : `${days}д назад`
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru', { day: 'numeric', month: 'short' })
}

// Срок годности initData, переданной query-параметром. Должен совпадать с
// _INIT_DATA_TTL_QUERY на бэкенде (app/api/auth.py).
const QUERY_INIT_DATA_TTL_SEC = 15 * 60

/** true, если initData уже старше срока годности query-ссылок. */
function isInitDataStale(): boolean {
  const authDate = new URLSearchParams(WebApp.initData).get('auth_date')
  if (!authDate) return false
  const issuedAt = Number(authDate)
  if (!Number.isFinite(issuedAt)) return false
  return Date.now() / 1000 - issuedAt > QUERY_INIT_DATA_TTL_SEC
}

// ── Tab type ─────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'users' | 'costs' | 'revenue' | 'system'

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-surface-1 border border-line-1 rounded-lg p-3 text-center">
      <p className="font-display text-2xl text-ink leading-none">{value}</p>
      <p className="text-xs text-ink-2 mt-1">{label}</p>
      {sub && <p className="text-xs text-accent mt-0.5">{sub}</p>}
    </div>
  )
}

function SubBadge({ tier }: { tier: string }) {
  if (tier === 'pro') return (
    <span className="text-xs font-medium text-white bg-accent px-1.5 py-0.5 rounded-full">Pro</span>
  )
  if (tier === 'plus') return (
    <span className="text-xs font-medium text-accent bg-accent-dim px-1.5 py-0.5 rounded-full">Plus</span>
  )
  return <span className="text-xs text-ink-2">Free</span>
}

// ── User row ──────────────────────────────────────────────────────────────────

function UserRow({
  user,
  onGrant,
  onRevoke,
  isLoading,
  onClick,
}: {
  user: AdminUser
  onGrant: (id: number, tier: string, days: number | null) => void
  onRevoke: (id: number) => void
  isLoading: boolean
  onClick: (id: number) => void
}) {
  const name = user.first_name ?? '—'
  const uname = user.username ? `@${user.username}` : `ID:${user.telegram_id}`

  return (
    <div
      className="bg-surface-1 border border-line-1 rounded-lg p-3 cursor-pointer btn-active"
      onClick={() => onClick(user.telegram_id)}
    >
      <div className="flex items-center gap-2">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-accent-dim flex items-center justify-center shrink-0">
          <span className="text-sm font-medium text-accent">{name[0]?.toUpperCase() ?? '?'}</span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-ink truncate">{name}</p>
            <SubBadge tier={user.subscription_tier} />
            {user.is_blocked && (
              <span className="text-xs text-danger">🔒</span>
            )}
          </div>
          <p className="text-xs text-ink-2 truncate">{uname} · {relativeTime(user.last_active_at)}</p>
        </div>

        {/* Actions — быстрое действие; полный выбор тарифа в детальной панели */}
        <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {user.subscription_tier === 'free' ? (
            <button
              className="text-xs font-medium text-accent bg-accent-dim px-2 py-1 rounded-md btn-active disabled:opacity-40"
              onClick={() => onGrant(user.telegram_id, 'pro', 30)}
              disabled={isLoading}
            >
              Pro 30д
            </button>
          ) : (
            <button
              className="text-xs font-medium text-danger bg-danger-dim px-2 py-1 rounded-md btn-active disabled:opacity-40"
              onClick={() => onRevoke(user.telegram_id)}
              disabled={isLoading}
            >
              Отозвать
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── User detail panel ─────────────────────────────────────────────────────────

function UserDetailPanel({
  telegramId,
  onClose,
  onGrant,
  onRevoke,
  onBlock,
  onUnblock,
}: {
  telegramId: number
  onClose: () => void
  onGrant: (id: number, tier: string, days: number | null) => void
  onRevoke: (id: number) => void
  onBlock: (id: number) => void
  onUnblock: (id: number) => void
}) {
  const [showMessage, setShowMessage] = useState(false)
  const [messageText, setMessageText] = useState('')

  const { data, isLoading, error } = useQuery<AdminUserDetail>({
    queryKey: ['admin-user', telegramId],
    queryFn: () => api.get(`/api/admin/users/${telegramId}`),
    staleTime: 30_000,
  })

  const sendMessageMut = useMutation({
    mutationFn: (text: string) =>
      api.post(`/api/admin/users/${telegramId}/message`, { text }),
    onSuccess: () => {
      setMessageText('')
      setShowMessage(false)
    },
  })

  if (isLoading) return (
    <div className="space-y-2 py-4">
      {[1, 2, 3].map(i => <RowSkeleton key={i} />)}
    </div>
  )
  if (error || !data) return <ErrorCard onRetry={() => {}} />

  const name = data.first_name ?? '—'
  const uname = data.username ? `@${data.username}` : `ID:${data.telegram_id}`
  const isPaid = data.subscription_tier !== 'free'

  return (
    <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-h3 text-ink">{name}</p>
          <p className="text-sm text-ink-2">{uname}</p>
          <p className="text-xs text-ink-2 mt-0.5">
            {data.is_blocked ? '🔒 Заблокирован · ' : ''}
            Зарегистрирован {formatDate(data.created_at)}
          </p>
        </div>
        <button onClick={onClose} className="text-ink-2 text-2xl leading-none px-1 btn-active">×</button>
      </div>

      {/* Subscription */}
      <div className="bg-surface-2 rounded-lg p-3">
        <p className="text-xs text-ink-2 mb-1">Подписка</p>
        <div className="flex items-center gap-2">
          <SubBadge tier={data.subscription_tier} />
          {isPaid && data.subscription_expires_at && (
            <span className="text-xs text-ink-2">до {formatDate(data.subscription_expires_at)}</span>
          )}
          {isPaid && !data.subscription_expires_at && (
            <span className="text-xs text-ink-2">бессрочно</span>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="Сообщений" value={data.messages_count} />
        <MetricCard label="Фактов" value={data.facts_count} />
        <MetricCard label="Задач" value={`${data.tasks_done}/${data.tasks_total}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="Напомин." value={data.reminders_active} />
        <MetricCard label="Расходы 30д" value={`$${data.cost_30d_usd.toFixed(4)}`} />
      </div>

      {/* Grant tier */}
      <div>
        <p className="text-xs text-ink-2 mb-1.5">Выдать тариф (30 дней)</p>
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" variant="secondary" onClick={() => onGrant(data.telegram_id, 'plus', 30)}>Plus</Button>
          <Button size="sm" variant="secondary" onClick={() => onGrant(data.telegram_id, 'pro', 30)}>Pro</Button>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" onClick={() => onGrant(data.telegram_id, 'pro', null)}>Pro ∞</Button>
        {isPaid && (
          <Button size="sm" variant="danger" onClick={() => onRevoke(data.telegram_id)}>Отозвать</Button>
        )}
        {data.is_blocked
          ? <Button size="sm" onClick={() => onUnblock(data.telegram_id)}>Разблокировать</Button>
          : <Button size="sm" variant="danger" onClick={() => onBlock(data.telegram_id)}>Заблокировать</Button>
        }
      </div>

      {/* Message */}
      {!showMessage && (
        <Button size="sm" variant="secondary" onClick={() => setShowMessage(true)}>
          Написать
        </Button>
      )}

      {showMessage && (
        <div className="space-y-2">
          <textarea
            className="w-full bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-2 outline-none focus:border-line-2 resize-none"
            rows={3}
            placeholder="Сообщение пользователю…"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => sendMessageMut.mutate(messageText)}
              disabled={!messageText.trim() || sendMessageMut.isPending}
            >
              {sendMessageMut.isPending ? 'Отправляю…' : 'Отправить'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowMessage(false)}>
              Отмена
            </Button>
          </div>
          {sendMessageMut.isSuccess && (
            <p className="text-xs text-accent">✓ Отправлено</p>
          )}
          {sendMessageMut.isError && (
            <p className="text-xs text-danger">Ошибка отправки</p>
          )}
        </div>
      )}

      {/* Export */}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          // initData в query живёт 15 минут (см. _INIT_DATA_TTL_QUERY на бэке):
          // URL уходит в новую вкладку и оседает в логах и в истории браузера.
          // Предупреждаем заранее — иначе админ увидит сырой 401.
          if (isInitDataStale()) {
            WebApp.showAlert(
              'Сессия устарела: закрой и открой админку заново, потом повтори экспорт.',
            )
            return
          }
          const initData = encodeURIComponent(WebApp.initData)
          window.open(
            `${_API_BASE}/api/admin/export/${data.telegram_id}?init_data=${initData}`,
            '_blank',
          )
        }}
      >
        Экспорт данных
      </Button>
    </div>
  )
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab() {
  const { data: stats, isLoading, error, refetch } = useQuery<AdminStats>({
    queryKey: ['admin-stats'],
    queryFn: () => api.get('/api/admin/stats'),
    staleTime: 60_000,
  })

  const { data: activity } = useQuery<AdminActivityUser[]>({
    queryKey: ['admin-activity'],
    queryFn: () => api.get('/api/admin/activity?hours=168'), // 7 дней
    staleTime: 60_000,
  })

  if (isLoading) return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map(i => <RowSkeleton key={i} />)}
    </div>
  )
  if (error) return <ErrorCard onRetry={refetch} />
  if (!stats) return null

  // График активности за 7 дней
  const activityByDay: Record<string, number> = {}
  if (activity) {
    activity.forEach(u => {
      const date = new Date(u.last_active_at).toISOString().split('T')[0]
      activityByDay[date] = (activityByDay[date] || 0) + 1
    })
  }
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date()
    d.setDate(d.getDate() - (6 - i))
    return d.toISOString().split('T')[0]
  })
  const activityCounts = last7Days.map(d => activityByDay[d] || 0)
  const maxActivity = Math.max(...activityCounts, 1)

  return (
    <div className="space-y-4">
      {/* Metrics 2×3 */}
      <div className="grid grid-cols-2 gap-2">
        <MetricCard
          label="Всего пользователей"
          value={stats.total_users}
          sub={`+${stats.new_7d} за 7 дней`}
        />
        <MetricCard label="Активных сегодня" value={stats.active_today} />
        <MetricCard label="Новых сегодня" value={stats.new_today} />
        <MetricCard label="Платящих" value={stats.paid_users} />
        <MetricCard label="Конверсия" value={`${stats.conversion_rate}%`} />
        <MetricCard
          label="Расходы 7д"
          value={`$${stats.cost_7d_usd.toFixed(3)}`}
          sub={`${stats.revenue_7d_stars} Stars`}
        />
      </div>

      {/* Messages 24h */}
      <div className="bg-surface-1 border border-line-1 rounded-lg p-3">
        <p className="text-xs font-medium text-ink-2 uppercase tracking-wider mb-2">Сообщений за 24ч</p>
        <p className="font-display text-3xl text-ink">{stats.messages_24h}</p>
      </div>

      {/* Активность за 7 дней (bar chart) */}
      <div className="bg-surface-1 border border-line-1 rounded-lg p-3">
        <p className="text-xs font-medium text-ink-2 uppercase tracking-wider mb-3">Активность за 7 дней</p>
        <div className="flex items-end justify-between gap-1 h-24">
          {activityCounts.map((count, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-surface-2 rounded-t overflow-hidden flex items-end" style={{ height: '80px' }}>
                <div
                  className="w-full bg-accent rounded-t transition-all"
                  style={{ height: `${(count / maxActivity) * 100}%` }}
                />
              </div>
              <span className="text-xs text-ink-2">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Misc */}
      <div className="bg-surface-1 border border-line-1 rounded-lg p-3 space-y-1.5">
        <p className="text-xs font-medium text-ink-2 uppercase tracking-wider">Прочее</p>
        <p className="text-sm text-ink-1">Активных за 7д: <span className="text-ink font-medium">{stats.active_7d}</span></p>
        <p className="text-sm text-ink-1">Фактов в памяти: <span className="text-ink font-medium">{stats.facts_total}</span></p>
        <p className="text-sm text-ink-1">Выручка 7д: <span className="text-ink font-medium">{stats.revenue_7d_stars} Stars (~${(stats.revenue_7d_stars * 0.013).toFixed(2)})</span></p>
      </div>
    </div>
  )
}

// ── Users Tab ─────────────────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useQuery<PaginatedUsers>({
    queryKey: ['admin-users', query, tierFilter, page],
    queryFn: () => api.get(`/api/admin/users?search=${encodeURIComponent(query)}&tier=${tierFilter}&page=${page}&limit=20`),
    staleTime: 30_000,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-users'] })
    qc.invalidateQueries({ queryKey: ['admin-user'] })
    qc.invalidateQueries({ queryKey: ['admin-stats'] })
  }

  const grantMut = useMutation({
    mutationFn: ({ id, tier, days }: { id: number; tier: string; days: number | null }) =>
      api.post(`/api/admin/users/${id}/grant`, { tier, days }),
    onSuccess: invalidate,
  })

  const revokeMut = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/users/${id}/revoke`),
    onSuccess: invalidate,
  })

  const blockMut = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/users/${id}/block`),
    onSuccess: invalidate,
  })

  const unblockMut = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/users/${id}/unblock`),
    onSuccess: invalidate,
  })

  const isActionLoading =
    grantMut.isPending || revokeMut.isPending || blockMut.isPending || unblockMut.isPending

  const handleSearch = () => {
    setPage(1)
    setQuery(search)
  }

  const totalPages = data ? Math.ceil(data.total / 20) : 1

  const tiers: Array<{ id: string; label: string }> = [
    { id: '', label: 'Все' },
    { id: 'free', label: 'Free' },
    { id: 'plus', label: 'Plus' },
    { id: 'pro', label: 'Pro' },
    { id: 'blocked', label: 'Заблок.' },
  ]

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="flex gap-2">
        <input
          className="flex-1 bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-2 outline-none focus:border-line-2 transition-colors"
          placeholder="Username, имя или telegram_id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button size="sm" onClick={handleSearch}>Найти</Button>
      </div>

      {/* Tier filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {tiers.map(t => (
          <button
            key={t.id}
            onClick={() => {
              setTierFilter(t.id)
              setPage(1)
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
              tierFilter === t.id
                ? 'bg-accent text-white'
                : 'bg-surface-1 text-ink-2 border border-line-1'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Selected user detail */}
      {selectedId !== null && (
        <UserDetailPanel
          telegramId={selectedId}
          onClose={() => setSelectedId(null)}
          onGrant={(id, tier, days) => grantMut.mutate({ id, tier, days })}
          onRevoke={(id) => revokeMut.mutate(id)}
          onBlock={(id) => blockMut.mutate(id)}
          onUnblock={(id) => unblockMut.mutate(id)}
        />
      )}

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => <RowSkeleton key={i} />)}
        </div>
      )}

      {error && <ErrorCard onRetry={refetch} />}

      {/* Users list */}
      {!isLoading && data && (
        <div className="space-y-2">
          {data.items.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              onGrant={(id, tier, days) => grantMut.mutate({ id, tier, days })}
              onRevoke={(id) => revokeMut.mutate(id)}
              isLoading={isActionLoading}
              onClick={(id) => setSelectedId(id === selectedId ? null : id)}
            />
          ))}
          {data.items.length === 0 && (
            <p className="text-sm text-ink-2 text-center py-8">Пользователи не найдены</p>
          )}
        </div>
      )}

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <button
            className="text-sm text-accent disabled:text-ink-2"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Назад
          </button>
          <p className="text-sm text-ink-2">{page} / {totalPages} · {data.total} польз.</p>
          <button
            className="text-sm text-accent disabled:text-ink-2"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages}
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  )
}

// ── Costs Tab ─────────────────────────────────────────────────────────────────

function CostsTab() {
  const [days, setDays] = useState<7 | 30 | 90>(7)

  const { data, isLoading, error, refetch } = useQuery<AdminUsage>({
    queryKey: ['admin-usage', days],
    queryFn: () => api.get(`/api/admin/usage?days=${days}`),
    staleTime: 60_000,
  })

  const featureNames: Record<string, string> = {
    chat: 'Chat GPT',
    extract_facts: 'Память',
    voice_in: 'Голос (STT)',
    voice_out: 'Голос (TTS)',
    tts: 'TTS',
    search: 'Поиск',
    embedding: 'Эмбеддинги',
    summarize: 'Саммари',
  }

  const totalCost = data?.total_cost_usd ?? 0
  const revenueUsd = (data?.total_revenue_stars ?? 0) * 0.013
  const margin = revenueUsd > 0 ? Math.round(((revenueUsd - totalCost) / revenueUsd) * 100) : null

  return (
    <div className="space-y-4">
      {/* Period toggle */}
      <div className="flex bg-surface-1 border border-line-1 rounded-xl p-1 gap-0.5">
        {([7, 30, 90] as const).map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              days === d ? 'bg-accent text-white' : 'text-ink-1'
            }`}
          >
            {d}д
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <RowSkeleton key={i} />)}
        </div>
      )}

      {error && <ErrorCard onRetry={refetch} />}

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2">
            <MetricCard label="Расходы" value={`$${totalCost.toFixed(3)}`} />
            <MetricCard
              label="Выручка"
              value={`$${revenueUsd.toFixed(2)}`}
              sub={`${data.total_revenue_stars} ★`}
            />
            <MetricCard label="Маржа" value={margin !== null ? `${margin}%` : '—'} />
          </div>

          {/* By feature */}
          {Object.keys(data.by_feature).length > 0 && (
            <div className="bg-surface-1 border border-line-1 rounded-lg p-3">
              <p className="text-xs font-medium text-ink-2 uppercase tracking-wider mb-3">По фичам</p>
              <div className="space-y-2.5">
                {Object.entries(data.by_feature)
                  .sort((a, b) => b[1] - a[1])
                  .map(([feat, cost]) => {
                    const pct = totalCost > 0 ? Math.round((cost / totalCost) * 100) : 0
                    return (
                      <div key={feat}>
                        <div className="flex justify-between text-sm mb-0.5">
                          <span className="text-ink-1">{featureNames[feat] ?? feat}</span>
                          <span className="text-ink font-medium">${cost.toFixed(4)} ({pct}%)</span>
                        </div>
                        <div className="h-1 bg-surface-2 rounded-full overflow-hidden">
                          <div className="h-full bg-accent rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {/* Top users */}
          {data.top_users.length > 0 && (
            <div className="bg-surface-1 border border-line-1 rounded-lg p-3">
              <p className="text-xs font-medium text-ink-2 uppercase tracking-wider mb-3">Топ по расходам</p>
              <div className="space-y-2">
                {data.top_users.slice(0, 10).map((u, i) => (
                  <div key={u.user_id} className="flex items-center gap-2">
                    <span className="text-xs text-ink-2 w-5 shrink-0">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink truncate">
                        {u.username ? `@${u.username}` : u.first_name ?? `ID:${u.telegram_id}`}
                      </p>
                      <p className="text-xs text-ink-2">{u.total_calls} запросов</p>
                    </div>
                    <span className="text-sm font-medium text-ink">${u.total_cost_usd.toFixed(4)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Revenue Tab ───────────────────────────────────────────────────────────────

function RevenueTab() {
  const { data, isLoading, error, refetch } = useQuery<RevenueData>({
    queryKey: ['admin-revenue'],
    queryFn: () => api.get('/api/admin/revenue'),
    staleTime: 60_000,
  })
  // Разбивка MRR должна сходиться с расчётом бэкенда — берём те же цены.
  const pricing = usePricing()
  const plusRub = pricing.plan('plus')?.rub
  const proRub = pricing.plan('pro')?.rub

  if (isLoading) return (
    <div className="space-y-2">
      {[1, 2, 3].map(i => <RowSkeleton key={i} />)}
    </div>
  )
  if (error) return <ErrorCard onRetry={refetch} />
  if (!data) return null

  const maxDaily = Math.max(...data.daily.map(d => d.amount_rub), 1)

  return (
    <div className="space-y-4">
      {/* Метрики */}
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="MRR" value={`${data.mrr_rub.toLocaleString('ru')} ₽`} />
        <MetricCard label="Этот месяц" value={`${data.current_month_rub.toLocaleString('ru')} ₽`} />
        <MetricCard label="Прошлый месяц" value={`${data.last_month_rub.toLocaleString('ru')} ₽`} />
      </div>

      {/* По тарифам */}
      <div className="bg-surface-1 border border-line-1 rounded-lg p-3">
        <p className="text-xs font-medium text-ink-2 uppercase tracking-wider mb-3">Подписки</p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-ink-1">Plus</span>
            <span className="text-ink font-medium">
              {data.by_tier.plus} × {plusRub === undefined ? '…' : `${plusRub}₽`}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ink-1">Pro</span>
            <span className="text-ink font-medium">
              {data.by_tier.pro} × {proRub === undefined ? '…' : `${proRub}₽`}
            </span>
          </div>
        </div>
      </div>

      {/* График дохода за 30 дней */}
      {data.daily.length > 0 && (
        <div className="bg-surface-1 border border-line-1 rounded-lg p-3">
          <p className="text-xs font-medium text-ink-2 uppercase tracking-wider mb-3">Доход 30 дней</p>
          <div className="flex items-end justify-between gap-1 h-32">
            {data.daily.map((d, i) => {
              const isLabelDay = i % 5 === 0
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-surface-2 rounded-t overflow-hidden flex items-end" style={{ height: '100px' }}>
                    <div
                      className="w-full bg-accent rounded-t transition-all"
                      style={{ height: `${(d.amount_rub / maxDaily) * 100}%` }}
                    />
                  </div>
                  {isLabelDay && (
                    <span className="text-xs text-ink-2">{formatDate(d.date)}</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── System Tab ────────────────────────────────────────────────────────────────

function SystemTab() {
  const qc = useQueryClient()
  const [broadcastText, setBroadcastText] = useState('')
  const [broadcastConfirm, setBroadcastConfirm] = useState(false)

  // Promocodes
  const [promoCode, setPromoCode] = useState('')
  const [promoType, setPromoType] = useState<'discount' | 'days'>('discount')
  const [promoValue, setPromoValue] = useState('')
  const [promoLimit, setPromoLimit] = useState('')

  const { data: backups, isLoading: backupsLoading } = useQuery<AdminBackup[]>({
    queryKey: ['admin-backups'],
    queryFn: () => api.get('/api/admin/backups'),
    staleTime: 120_000,
  })

  const { data: dbStats, isLoading: dbLoading } = useQuery<AdminDbStat[]>({
    queryKey: ['admin-db-stats'],
    queryFn: () => api.get('/api/admin/db-stats'),
    staleTime: 120_000,
  })

  const { data: promos, isLoading: promosLoading } = useQuery<PromoSchema[]>({
    queryKey: ['admin-promos'],
    queryFn: () => api.get('/api/admin/promos'),
    staleTime: 60_000,
  })

  const backupMut = useMutation({
    mutationFn: () => api.post('/api/admin/backup'),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ['admin-backups'] }), 10_000)
    },
  })

  const dryRunMut = useMutation({
    mutationFn: (text: string) =>
      api.post<BroadcastResult>('/api/admin/broadcast', { text, confirm: false }),
  })

  const broadcastMut = useMutation({
    mutationFn: (text: string) =>
      api.post<BroadcastResult>('/api/admin/broadcast', { text, confirm: true }),
    onSuccess: () => {
      setBroadcastText('')
      setBroadcastConfirm(false)
    },
  })

  const createPromoMut = useMutation({
    mutationFn: () =>
      api.post('/api/admin/promos', {
        code: promoCode,
        promo_type: promoType,
        discount_percent: promoType === 'discount' ? parseInt(promoValue) : null,
        days: promoType === 'days' ? parseInt(promoValue) : null,
        max_uses: parseInt(promoLimit),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-promos'] })
      setPromoCode('')
      setPromoValue('')
      setPromoLimit('')
    },
  })

  return (
    <div className="space-y-4">
      {/* Backup */}
      <section>
        <p className="section-label">Резервные копии</p>
        <div className="bg-surface-1 border border-line-1 rounded-lg p-4 space-y-3">
          <Button
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={() => backupMut.mutate()}
            disabled={backupMut.isPending}
          >
            {backupMut.isPending ? 'Запускаю…' : backupMut.isSuccess ? 'Запущен ✓' : 'Запустить бэкап'}
          </Button>

          {backupsLoading && <p className="text-sm text-ink-2">Загрузка…</p>}

          {backups && backups.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {backups.slice(0, 8).map(b => (
                <div key={b.key} className="flex items-center justify-between text-sm">
                  <span className="text-ink-1 truncate flex-1 text-xs font-mono">
                    {b.key.replace('daily/', '')}
                  </span>
                  <span className="text-ink-2 shrink-0 ml-2 text-xs">{formatBytes(b.size_bytes)}</span>
                </div>
              ))}
            </div>
          )}
          {backups?.length === 0 && (
            <p className="text-sm text-ink-2">S3 не настроен или бэкапов нет.</p>
          )}
        </div>
      </section>

      {/* DB Stats */}
      <section>
        <p className="section-label">Таблицы БД</p>
        <div className="bg-surface-1 border border-line-1 rounded-lg overflow-hidden">
          {dbLoading && <div className="p-4"><RowSkeleton /></div>}
          {dbStats?.slice(0, 8).map((t, i) => (
            <div
              key={t.table_name}
              className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-line' : ''}`}
            >
              <span className="text-ink-1 font-mono text-xs">{t.table_name}</span>
              <div className="text-right">
                <span className="text-sm text-ink">{t.row_count.toLocaleString('ru')}</span>
                <span className="text-xs text-ink-2 ml-2">{formatBytes(t.size_bytes)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Promocodes */}
      <section>
        <p className="section-label">Промокоды</p>
        <div className="bg-surface-1 border border-line-1 rounded-lg p-4 space-y-3">
          {/* Форма создания */}
          <div className="space-y-2">
            <input
              className="w-full bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-2 outline-none focus:border-line-2"
              placeholder="Код (DELO20)"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            />
            <div className="flex gap-2">
              <select
                className="flex-1 bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink outline-none focus:border-line-2"
                value={promoType}
                onChange={(e) => setPromoType(e.target.value as 'discount' | 'days')}
              >
                <option value="discount">Скидка %</option>
                <option value="days">Дни подписки</option>
              </select>
              <input
                type="number"
                className="flex-1 bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-2 outline-none focus:border-line-2"
                placeholder="Значение"
                value={promoValue}
                onChange={(e) => setPromoValue(e.target.value)}
              />
              <input
                type="number"
                className="flex-1 bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-2 outline-none focus:border-line-2"
                placeholder="Лимит"
                value={promoLimit}
                onChange={(e) => setPromoLimit(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              onClick={() => createPromoMut.mutate()}
              disabled={!promoCode || !promoValue || !promoLimit || createPromoMut.isPending}
            >
              {createPromoMut.isPending ? 'Создаю…' : 'Создать'}
            </Button>
            {createPromoMut.isSuccess && (
              <p className="text-xs text-accent">✓ Промокод создан</p>
            )}
            {createPromoMut.isError && (
              <p className="text-xs text-danger">Ошибка создания</p>
            )}
          </div>

          {/* Список */}
          {promosLoading && <p className="text-sm text-ink-2">Загрузка…</p>}
          {promos && promos.length > 0 && (
            <div className="space-y-1.5 pt-1 border-t border-line-1">
              {promos.map(p => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={p.is_active ? 'text-accent' : 'text-ink-2'}>
                      {p.is_active ? '✅' : '❌'}
                    </span>
                    <span className="font-mono text-xs text-ink">{p.code}</span>
                    <span className="text-xs text-ink-2">
                      {p.promo_type === 'discount' ? `${p.discount_percent}%` : `${p.days}д`}
                    </span>
                  </div>
                  <span className="text-xs text-ink-2">
                    {p.uses_count}/{p.max_uses ?? '∞'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {promos?.length === 0 && (
            <p className="text-sm text-ink-2">Промокодов нет.</p>
          )}
        </div>
      </section>

      {/* Broadcast */}
      <section>
        <p className="section-label">Рассылка</p>
        <div className="bg-surface-1 border border-line-1 rounded-lg p-4 space-y-3">
          <textarea
            className="w-full bg-surface-2 border border-line-1 rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-2 outline-none focus:border-line-2 resize-none transition-colors"
            rows={4}
            placeholder="Текст рассылки…"
            value={broadcastText}
            onChange={(e) => {
              setBroadcastText(e.target.value)
              setBroadcastConfirm(false)
            }}
          />

          {broadcastText.trim() && !broadcastConfirm && (
            <Button
              size="sm"
              onClick={() => dryRunMut.mutate(broadcastText)}
              disabled={dryRunMut.isPending}
            >
              Проверить охват
            </Button>
          )}

          {dryRunMut.isSuccess && !broadcastConfirm && (
            <div className="bg-surface-2 rounded-lg p-3 space-y-2">
              <p className="text-sm text-ink">
                Получателей: <strong>{(dryRunMut.data as BroadcastResult)?.total ?? 0}</strong>
              </p>
              <Button size="sm" variant="danger" onClick={() => setBroadcastConfirm(true)}>
                Разослать всем
              </Button>
            </div>
          )}

          {broadcastConfirm && (
            <div className="bg-danger-dim border border-line-1 rounded-lg p-3 space-y-2">
              <p className="text-sm text-danger font-medium">Разослать всем пользователям?</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => broadcastMut.mutate(broadcastText)}
                  disabled={broadcastMut.isPending}
                >
                  {broadcastMut.isPending ? 'Рассылаю…' : 'Да, разослать'}
                </Button>
                <Button size="sm" onClick={() => setBroadcastConfirm(false)}>Отмена</Button>
              </div>
            </div>
          )}

          {broadcastMut.isSuccess && (
            <p className="text-sm text-accent">
              Готово. Отправлено: {(broadcastMut.data as BroadcastResult)?.sent ?? 0},
              не доставлено: {(broadcastMut.data as BroadcastResult)?.failed ?? 0}
            </p>
          )}
        </div>
      </section>
    </div>
  )
}

// ── Main Admin component ──────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Обзор', icon: <BarChart2 size={16} /> },
  { id: 'users', label: 'Пользователи', icon: <Users size={16} /> },
  { id: 'costs', label: 'Расходы', icon: <Zap size={16} /> },
  { id: 'revenue', label: 'Revenue', icon: <TrendingUp size={16} /> },
  { id: 'system', label: 'Система', icon: <Server size={16} /> },
]

export function Admin() {
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  return (
    <div className="flex flex-col min-h-full page-enter">
      {/* Header */}
      <div className="px-4 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-accent" />
          <p className="text-h1 text-ink">Admin Panel</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="px-4 mb-4">
        <div className="flex bg-surface-1 border border-line-1 rounded-xl p-1 gap-0.5">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg text-xs font-medium transition-colors btn-active ${
                activeTab === tab.id
                  ? 'bg-accent text-white'
                  : 'text-ink-2'
              }`}
            >
              {tab.icon}
              <span className="leading-none">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 px-4 pb-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'costs' && <CostsTab />}
        {activeTab === 'revenue' && <RevenueTab />}
        {activeTab === 'system' && <SystemTab />}
      </div>
    </div>
  )
}
